import { useMutation } from '@tanstack/react-query';
import { CasesApiClient } from '../api/cases-api-client';
import { ResultValue } from '@@/types/ApiResult';
import type { CreateCaseInput, UpdateCaseInput, CaseResponse } from '../types/api.types';
import { toast } from 'sonner';

interface UseCaseMutationOptions {
  onSuccess?: (data: CaseResponse) => void;
  onError?: (error: Error) => void;
}

export const useCreateCase = (options?: UseCaseMutationOptions) => {
  return useMutation({
    mutationFn: async (payload: CreateCaseInput): Promise<CaseResponse> => {
      const client = new CasesApiClient();
      const result = await client.createCase(payload);

      if (ResultValue.error(result)) {
        throw new Error(result.message || 'Failed to create case');
      }

      if (!result.data) {
        throw new Error('No case data returned');
      }

      return result.data;
    },
    onSuccess: (data) => {
      toast.success('Case created successfully', {
        description: `Case#${data.case_id} has been created successfully`,
        duration: 5000
      });

      options?.onSuccess?.(data);
    },
    onError: (error) => {
      console.error('CreateCase error', error);
      toast.error('Failed to create case', {
        description: error instanceof Error ? error.message : 'Unknown error',
        duration: 5000
      });

      options?.onError?.(error instanceof Error ? error : new Error('Unknown error'));
    }
  });
};

export const useUpdateCase = (options?: UseCaseMutationOptions) => {
  return useMutation({
    mutationFn: async (payload: UpdateCaseInput): Promise<CaseResponse> => {
      const client = new CasesApiClient();
      const result = await client.updateCase(payload);

      if (ResultValue.error(result)) {
        throw new Error(result.message || 'Failed to update case');
      }

      if (!result.data) {
        throw new Error('No case data returned');
      }

      return result.data;
    },
    onSuccess: (data) => {
      toast.success('Case updated successfully', {
        description: `Case#${data.case_id} has been updated successfully`,
        duration: 5000
      });

      options?.onSuccess?.(data);
    },
    onError: (error) => {
      console.error('UpdateCase error', error);
      toast.error('Failed to update case', {
        description: error instanceof Error ? error.message : 'Unknown error',
        duration: 5000
      });

      options?.onError?.(error instanceof Error ? error : new Error('Unknown error'));
    }
  });
};
